import type { Unsubscribe } from "./api";

export type Handler<T> = (payload: T) => void;

export type Emitter<Events extends Record<string, unknown>> = {
  on<K extends keyof Events>(event: K, handler: Handler<Events[K]>): Unsubscribe;
  once<K extends keyof Events>(event: K, handler: Handler<Events[K]>): Unsubscribe;
  off<K extends keyof Events>(event: K, handler: Handler<Events[K]>): void;
  emit<K extends keyof Events>(event: K, payload: Events[K]): void;
  clear(): void;
};

export function createEmitter<Events extends Record<string, unknown>>(): Emitter<Events> {
  const handlers = new Map<keyof Events, Set<Handler<unknown>>>();

  const on = <K extends keyof Events>(event: K, handler: Handler<Events[K]>): Unsubscribe => {
    let set = handlers.get(event);

    if (set === undefined) {
      set = new Set();
      handlers.set(event, set);
    }

    set.add(handler as Handler<unknown>);

    return () => off(event, handler);
  };

  const off = <K extends keyof Events>(event: K, handler: Handler<Events[K]>): void => {
    handlers.get(event)?.delete(handler as Handler<unknown>);
  };

  const once = <K extends keyof Events>(event: K, handler: Handler<Events[K]>): Unsubscribe => {
    const wrapped: Handler<Events[K]> = payload => {
      off(event, wrapped);
      handler(payload);
    };

    return on(event, wrapped);
  };

  const emit = <K extends keyof Events>(event: K, payload: Events[K]): void => {
    const set = handlers.get(event);

    if (set === undefined) return;

    for (const handler of [...set]) handler(payload);
  };

  return {
    on,
    once,
    off,
    emit,
    clear: () => handlers.clear()
  };
}
